<template>
    <UiModal
        :is-open="open"
        @close="close()"
        :title="modalTitle"
        :description="modalDescription"
        :is-teleported="true"
        :z-index="9999999"
    >
        <template #content>
            <div class="space-y-4">
                <!-- Show dropdown only when adding new business (no ID) -->
                <UISelect
                    v-if="users.length > 0 && !businessModel.id"
                    v-model:inputValue="businessModel.user_id"
                    id="user-id"
                    label="Select user"
                    :errors="formErrors"
                    name="user_id"
                    :placeholder="'User'"
                    :value-key="'value'"
                    :select-options="usersOptions"
                />
                <!-- Show read-only display when editing existing business (has ID) -->
                <div v-else-if="users.length > 0 && businessModel.id" class="space-y-1">
                    <label class="block text-sm font-medium text-slate-600 dark:text-slate-300">User</label>
                    <div class="py-2 px-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-slate-100 text-sm">
                        {{ getAssociatedUserName() }}
                    </div>
                </div>
                <UIInput
                    v-model:inputValue="businessModel.name"
                    id="business-name"
                    type="text"
                    :errors="formErrors"
                    name="name"
                    label="Business name"
                    placeholder="Enter business name"
                />

                <UISelect
                    v-model:inputValue="businessModel.type"
                    id="type"
                    label="Business type"
                    :errors="formErrors"
                    name="type"
                    :placeholder="'Select business type'"
                    :value-key="'value'"
                    :select-options="[
                        {
                            value: 'sole-prop',
                            label: 'Sole Proprietorship',
                        },
                        { value: 'partnership', label: 'Partnership' },
                        { value: 'llc', label: 'Limited Liability Company' },
                        { value: 's-corp', label: 'S-Corp' },
                        { value: 'c-corp', label: 'C-Corp' },
                    ]"
                />
                <div>
                    <h5
                        class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    >
                        Tax ID type
                    </h5>
                    <div class="flex gap-4 items-center">
                        <label
                            class="flex items-center gap-1 text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight"
                        >
                            <input
                                type="radio"
                                name="tax_id_type"
                                value="ein"
                                class="form-radio"
                                v-model="businessModel.tax_id_type"
                            />
                            EIN
                        </label>
                        <label
                            class="flex items-center gap-1 text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight"
                        >
                            <input
                                type="radio"
                                name="tax_id_type"
                                value="ssn"
                                class="form-radio"
                                v-model="businessModel.tax_id_type"
                            />
                            SSN
                        </label>
                    </div>
                    <transition name="fade">
                        <div
                            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                            role="alert"
                            v-if="formErrors.tax_id_type"
                        >
                            <Icon icon="caution" />
                            {{ formErrors.tax_id_type }}
                        </div>
                    </transition>
                </div>
                <div>
                    <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Annual business spend <span class="text-slate-400 dark:text-slate-500">(optional)</span>
                    </label>
                    <div class="relative flex items-center border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700">
                        <span class="flex items-center justify-center px-4 py-1.5 text-slate-600 dark:text-slate-300 border-r border-slate-200 dark:border-slate-600">$</span>
                        <input
                            type="text"
                            v-model="businessModel.annual_business_spend"
                            placeholder="Enter amount"
                            class="flex-1 px-4 py-1.5 border-0 outline-none bg-transparent text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-400 rounded-r"
                            @input="formatBusinessSpend"
                        />
                    </div>
                    <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                        <Icon icon="info" :width="14" :height="14" />
                        Get best cards recommendations tailored to your business
                    </p>
                </div>
            </div>
        </template>
        <template #errors>
            <div class="flex justify-center">
                <Errors :errors="errors" />
            </div>
        </template>
        <template #footer>
            <div class="flex justify-end gap-4">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="close()"
                >
                    Close
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="addEditBusiness()"
                >
                    {{ actionButtonLabel }}
                </button>
            </div>
        </template>
    </UiModal>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UIInput from '@/components/ui/inputs/ui-input.vue';
import UISelect from '@/components/ui/inputs/ui-select.vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { notify } from 'notiwind';

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    business: {
        type: Object,
        default: null,
    },
    users: {
        type: Array,
        default: () => [],
    },
});

const businessModel = defineModel('businessModel', {
    default: () => ({
        user_id: null,
        name: null,
        type: null,
        tax_id_type: null,
        annual_business_spend: null,
    }),
});

if (!businessModel.value) {
    businessModel.value = {
        user_id: null,
        name: null,
        type: null,
        tax_id_type: null,
        annual_business_spend: null,
    };
}

const emit = defineEmits(['close', 'add-business', 'update-business']);

const modalTitle = computed(() => {
    return businessModel.value && businessModel.value.id
        ? 'Edit business entity'
        : 'Add business entity';
});

const modalDescription = computed(() => {
    return businessModel.value && businessModel.value.id
        ? ''
        : 'Create a business entity to keep track of all related cards.';
});

const actionButtonLabel = computed(() => {
    return businessModel.value && businessModel.value.id
        ? 'Save changes'
        : 'Add business';
});

const usersOptions = computed(() => {
    return props.users.map((user) => ({
        value: user.id,
        label: user.name,
    }));
});

const getAssociatedUserName = () => {
    if (!businessModel.value?.user_id) return 'Unknown User';
    const user = props.users.find((u) => u.id === businessModel.value.user_id);
    return user ? user.name : 'Unknown User';
};

const userIdToSend = computed(() => {
    if (!businessModel.value) return null;
    return props.users.length > 0 ? businessModel.value.user_id : null;
});

const close = () => {
    emit('close');
};

const addEditBusiness = () => {
    if (!validate()) {
        return;
    }
    if (businessModel.value && businessModel.value.id) {
        emit('update-business', {
            user_id: userIdToSend.value,
            ...businessModel.value,
        });
    } else {
        emit('add-business', {
            userId: userIdToSend.value,
            ...businessModel.value,
        });
    }
    close();
};

const errors = ref([]);
const formErrors = ref({});

const formatBusinessSpend = () => {
    const value = businessModel.value.annual_business_spend;
    if (!value) return;

    // Remove non-numeric characters except decimal point
    let cleaned = value.toString().replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
        cleaned = parts[0] + '.' + parts[1].substring(0, 2);
    }

    // Format with commas
    if (cleaned) {
        const [whole, decimal] = cleaned.split('.');
        const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        businessModel.value.annual_business_spend = decimal !== undefined ? formatted + '.' + decimal : formatted;
    }
};

const validate = () => {
    errors.value = [];
    formErrors.value = {};

    if (props.users.length > 0 && !businessModel.value.id && !businessModel.value.user_id) {
        errors.value.push({ message: 'User is required' });
        formErrors.value.user_id = 'User is required';
    }

    if (!businessModel.value?.name) {
        errors.value.push({ message: 'Business name is required' });
        formErrors.value.name = 'Business name is required';
    }

    if (!businessModel.value?.type) {
        errors.value.push({ message: 'Business type is required' });
        formErrors.value.type = 'Business type is required';
    }

    if (!businessModel.value?.tax_id_type) {
        errors.value.push({ message: 'Tax ID type is required' });
        formErrors.value.tax_id_type = 'Tax ID type is required';
    }

    return errors.value.length === 0;
};
</script>
